"use client";
import { useSelectedConversation } from "@/contexts/SelectedConversationContext";
import { Colors } from "@/enums/Colors";
import AuthService from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/services/AuthService";
import { LazoChatConversation } from "@/models/ai-chat/LazoChatConversation";
import LazoUserService from "@/services/LazoUserService";
import LazoChatMessageService from "@/services/ai-chat/LazoChatMessageService";
import { AttachFileOutlined, StopCircleOutlined } from "@mui/icons-material";
import { Box, styled } from "@mui/material";
import Button from "@mui/material/Button";
import { CSSProperties, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { TailSpin } from "react-loading-icons";
import { Mention, MentionItem, MentionsInput, SuggestionDataItem } from "react-mentions";
import { StarIcon } from "../utils/icons/StarIcon";
import PaperPlaneOutlined from "./../../../assets/images/paper-plane-send-icon.svg";
import { FirstTimeMultiUserChatMessage } from "./FirstTimeMultiUserChatMessage";

interface ChatTextFieldProps {
  messageReceiverIsBot: boolean;
  showAskAiToggleButton: boolean;
  isCreatingConversation: boolean;
  waiting: boolean;
  onSendMessage: (message: string, messageReceiverIsBot: boolean, mentions?: MentionItem[], files?: File[]) => void;
  onClickStop: () => void;
  handleToggleMessageReceiver: () => void;
  hasBeenInMultiuserChat?: boolean;
  handleChange?: (event: { target: { value: string } }, mentions: MentionItem[]) => void;
  selectedConversation?: LazoChatConversation;
  inputPlaceholder?: string;
}

const ChatButton = styled(Button)(({}) => ({
  padding: 0,
  margin: ".2rem",
  borderRadius: "50%",
  height: "1.5rem",
  minWidth: "1.5rem",
  "&.ask-ai-button": {
    backgroundColor: Colors.BUTTON_SECONDARY_PURPLE,
  },
}));

const MentionInputStyle = {
  margin: "1em 0",
  position: "absolute",
  left: "17px",
  width: "calc(100% - 17px)",
  height: "72px",
  backgroundColor: `${Colors.WHITE}`,
  maxWidth: "100%",

  control: {
    fontSize: "14px",
  },
  "&multiLine": {
    borderRadius: "16px",
    //border: `1px solid ${Colors.STONE_GRAY}`,

    height: "72px",
    highlighter: {
      padding: "16px 13px 16px 14.6px",
      maxHeight: "72px",
      height: "72px",
      width: "91%",
      borderRadius: "10px",
      border: "none",
      outline: "none",
      textOverflow: "ellipsis",
    },

    input: {
      padding: "18.8px 13px 14px 13.5px",
      height: "70px",
      width: "85%",
      borderRadius: "10px",
      border: "none",
      outline: "none",
      textOverflow: "ellipsis",
      fontWeight: "500",
      overflowWrap: "break-word",
      whiteSpace: "pre-wrap",
    },
  },

  suggestions: {
    list: {
      backgroundColor: "white",
      border: `1px solid ${Colors.LIGHT_GRAY}`,
      fontSize: "0.875rem",
      fontWeight: "500",
    },
    item: {
      padding: "5px 15px",
      borderBottom: `1px solid ${Colors.LIGHT_GRAY}`,
      "&focused": {
        backgroundColor: `${Colors.LAZO_LIGHT_PURPLE}`,
      },
    },
  },
};

const ChatTextFieldContainerStyle: CSSProperties = {
  display: "flex",
  width: "100%",
  height: "72px",
  // backgroundColor: Colors.WHITE,
  alignItems: "center",
  justifyContent: "end",
  padding: "0 0 14px 0",
  marginTop: "1rem",
};

const ChatTextFieldComponent = ({
  messageReceiverIsBot,
  showAskAiToggleButton,
  isCreatingConversation,
  waiting,
  onSendMessage,
  onClickStop,
  handleToggleMessageReceiver,
  hasBeenInMultiuserChat,
}: ChatTextFieldProps) => {
  const { t } = useTranslation("chat");
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { selectedConversation } = useSelectedConversation();
  const [files, setFiles] = useState<File[]>([]);
  const [message, setMessage] = useState<string>("");
  const [mentions, setMentions] = useState<MentionItem[]>([]);

  const [firstTimeMultiUserChatIsOpen, setFirstTimeMultiUserChatIsOpen] = useState(false);
  const [lastTypingTime, setLastTypingTime] = useState<number>(0);

  const handleChange = (event: { target: { value: string } }, mentions: MentionItem[]) => {
    setMessage(event.target.value);

    setMentions(mentions);

    if (selectedConversation?.externalId && new Date().getTime() - lastTypingTime >= 3000) {
      LazoChatMessageService.sendTypingEvent(selectedConversation?.externalId as string, event.target.value.trim() !== "");

      setLastTypingTime(new Date().getTime());
    }
  };

  const handleSendMessage = () => {
    if (message.trim() !== "" || files.length > 0) {
      //execute the onSendMessage function that was passed as a prop from the parent component
      onSendMessage(message, messageReceiverIsBot, mentions, files);
      setMessage("");
      setFiles([]);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setFiles([...files, e.target.files[0]]);
    }
  };

  const handleAttachFile = () => {
    fileInputRef.current?.click();
  };

  const handleKeyPress = (event: React.KeyboardEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    if (event.key === "Enter") {
      handleSendMessage();
    }
  };

  function mapMembersToMentions(): SuggestionDataItem[] {
    const suggestionsArray: SuggestionDataItem[] = [];
    const currentUserExternalId = AuthService.getUser()?.externalId;
    if (selectedConversation?.members) {
      selectedConversation.members.forEach((member) => {
        if (member.user.externalId !== currentUserExternalId) {
          suggestionsArray.push({
            id: member.user.externalId!,
            display: member.user.firstName + " " + member.user.lastName,
          });
        }
      });
    }
    return suggestionsArray;
  }

  useEffect(() => {
    if (!hasBeenInMultiuserChat) {
      setFirstTimeMultiUserChatIsOpen(true);
    }
  }, [hasBeenInMultiuserChat]);

  const updateUserFirstTimeMultiUserChat = async () => {
    const lazoUser = (await LazoUserService.getByCoreUser()).getContentOrThrowError();

    if (lazoUser.externalId) {
      LazoUserService.markUserInMultiUserChat(lazoUser.externalId);
    }
  };

  return (
    <>
      <Box
        sx={{
          height: files.length !== 0 ? "9.7375rem" : "5.4rem",
          flex: "100%",
          width: "100%",
          borderRadius: "1rem",
          position: "relative",
          minWidth: { xs: "0%", md: "%" },
          border: `1px solid ${Colors.STONE_GRAY}`,
          marginTop: messageReceiverIsBot ? "-2rem" : "0",
          zIndex: 2,
          backgroundColor: Colors.WHITE,
        }}
      >
        <div style={{ ...ChatTextFieldContainerStyle }}>
          {/* @ts-ignore */}
          <MentionsInput
            value={message}
            onChange={(ev, _, __, mentions) => handleChange(ev, mentions)}
            onKeyDown={handleKeyPress}
            allowSuggestionsAboveCursor
            forceSuggestionsAboveCursor
            allowSpaceInQuery
            // @ts-ignore
            style={{ ...MentionInputStyle }}
            disabled={waiting}
            placeholder={t("textField.placeholderGeneric")}
          >
            {/* @ts-ignore */}
            <Mention
              displayTransform={(_, display: string) => "@" + display}
              trigger={"@"}
              data={(e: string) => mapMembersToMentions().filter(({ display }) => display!.toLowerCase().startsWith(e.toLowerCase()))}
              className={"mentions__mention"}
              markup="@[__display__](__id__)"
            />
          </MentionsInput>
          <div
            style={{
              position: "absolute",
              display: "flex",
              alignItems: "center",
              zIndex: 100,
              paddingRight: "0.5rem",
              gap: "0.3rem",
            }}
          >
            {showAskAiToggleButton && firstTimeMultiUserChatIsOpen ? (
              <FirstTimeMultiUserChatMessage
                handleClose={() => {
                  setFirstTimeMultiUserChatIsOpen(false);
                }}
                handleConfirm={updateUserFirstTimeMultiUserChat}
              >
                {showAskAiToggleButton && (
                  <StarIcon
                    disableHoverEffect
                    staticPurple={messageReceiverIsBot}
                    displayText={false}
                    onClick={handleToggleMessageReceiver}
                  />
                )}
              </FirstTimeMultiUserChatMessage>
            ) : (
              showAskAiToggleButton && (
                <StarIcon
                  disableHoverEffect
                  staticPurple={messageReceiverIsBot}
                  displayText={false}
                  onClick={handleToggleMessageReceiver}
                />
              )
            )}

            {/* Removed temporarily */}
            {false && (
              <div onClick={handleAttachFile}>
                <input ref={fileInputRef} id="file" type="file" style={{ display: "none" }} onChange={handleFileChange} />
                <AttachFileOutlined
                  sx={{
                    transform: "scale(1.4) rotate(40deg)",
                    ":hover": { cursor: "pointer" },
                  }}
                />
              </div>
            )}
            {!waiting ? (
              <ChatButton variant="text" color="primary" onClick={handleSendMessage}>
                <img alt={t("textField.alt.sendButton")} src={PaperPlaneOutlined.src} />
              </ChatButton>
            ) : waiting && isCreatingConversation ? (
              <TailSpin stroke={Colors.STONE_GRAY} strokeOpacity={1} style={{ transform: "scale(0.8)" }} />
            ) : (
              <ChatButton variant="text" color="primary" disabled={!waiting} onClick={onClickStop}>
                <StopCircleOutlined />
              </ChatButton>
            )}
          </div>
        </div>
      </Box>
    </>
  );
};

export default ChatTextFieldComponent;
